import { useEffect, useRef, useState } from 'react';
import s from '../../styles/corporate/Corporate.module.css';

const REDUCED = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * 감싼 영역 안의 `.reveal` 요소를 화면에 들어올 때 한 번씩 보여준다.
 * data-delay 속성(ms)이 있으면 그만큼 늦춘다.
 */
export function useReveal() {
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const targets = Array.from(root.querySelectorAll(`.${s.reveal}`));
    const show = (el) => {
      const delay = Number(el.dataset.delay || 0);
      if (delay) el.style.transitionDelay = `${delay}ms`;
      el.classList.add(s.revealed);
    };

    if (typeof IntersectionObserver === 'undefined' || REDUCED()) {
      targets.forEach((el) => el.classList.add(s.revealed));
      return undefined;
    }

    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          show(entry.target);
          io.unobserve(entry.target);
        }),
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
    );
    targets.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return rootRef;
}

/** 화면에 들어오면 0 에서 target 까지 세어 올린다. */
export function useCountUp(target, duration = 1100) {
  const ref = useRef(null);
  const [value, setValue] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined' || REDUCED()) {
      setValue(target);
      return undefined;
    }

    let raf = 0;
    const run = () => {
      const start = performance.now();
      const tick = (now) => {
        const p = Math.min(1, (now - start) / duration);
        const eased = 1 - (1 - p) ** 4;
        setValue(Math.round(target * eased));
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        run();
      },
      { threshold: 0.3 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [duration, target]);

  return [ref, value];
}

/**
 * 요소가 화면을 지나는 동안의 진행도(0~1).
 * 제목 글자 채우기와 HIKS 글자 채우기에 쓴다.
 */
export function useScrollProgress() {
  const ref = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    let raf = 0;
    const measure = () => {
      raf = 0;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const p = (vh * 0.85 - rect.top) / (rect.height + vh * 0.35);
      setProgress(Math.min(1, Math.max(0, p)));
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return [ref, progress];
}

/**
 * 스크롤 위치에 따라 지금 보고 있는 단계(step)의 번호를 돌려준다.
 * 각 단계 요소에 ref 를 등록하는 setter 를 함께 준다.
 */
export function useActiveStep(count) {
  const refs = useRef([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    let raf = 0;
    const measure = () => {
      raf = 0;
      const line = window.innerHeight * 0.55;
      let next = 0;
      refs.current.forEach((el, i) => {
        if (el && el.getBoundingClientRect().top < line) next = i;
      });
      setActive(next);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [count]);

  const setRef = (i) => (el) => {
    refs.current[i] = el;
  };

  return [active, setRef];
}

/** 히어로 배경에서 마우스를 따라다니는 빛. 터치 기기에서는 쓰지 않는다. */
export function usePointerGlow() {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || REDUCED()) return undefined;
    if (window.matchMedia && !window.matchMedia('(hover: hover)').matches) return undefined;

    const onMove = (e) => {
      const rect = el.parentElement.getBoundingClientRect();
      el.style.transform = `translate(${e.clientX - rect.left}px, ${e.clientY - rect.top}px)`;
    };
    const host = el.parentElement;
    host.addEventListener('mousemove', onMove, { passive: true });
    return () => host.removeEventListener('mousemove', onMove);
  }, []);

  return ref;
}
